import React from 'react';
import { useSelector } from 'react-redux';
import { PopoverOrigin } from '@material-ui/core';
import { AutocompleteCloseReason } from '@material-ui/lab/Autocomplete';
import { sortBy } from 'lodash';
import { RootState } from '../../store';
import { User, UserRoles } from '../../constants';
import { AssigneeDropDownOptions, Company } from '../../store/clients/types';
import { SelectDropDown } from '../Select/SelectDropdown';

interface AssigneeDropDownProps {
  assigneeDropDownId?: string;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: (
    event: React.ChangeEvent<{}>,
    reason: AutocompleteCloseReason,
  ) => void;
  noOptionsText?: string;
  placeholder: string;
  isMultiple: boolean;
  lead?: User;
  assignees?: Array<User | Company>;
  leadUserId?: string;
  onChange: (
    props: AssigneeDropDownOptions | AssigneeDropDownOptions[],
  ) => void;
  disableCloseOnSelect: boolean;
  width?: string | number;
  anchorOrigin?: PopoverOrigin;
}

export const AssigneeDropDown: React.FC<AssigneeDropDownProps> = ({
  assigneeDropDownId,
  open,
  anchorEl,
  onClose,
  noOptionsText = 'No options',
  placeholder = '',
  isMultiple = false,
  lead,
  assignees,
  leadUserId,
  onChange,
  width: menuWidth,
  anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'left',
  },
  ...autocompleteOptions
}) => {
  const internalUsers = useSelector(
    (state: RootState) => state.users.internalUsers,
  );

  const [pendingValue, setPendingValue] = React.useState<any>([]);
  const [options, setOptions] = React.useState<AssigneeDropDownOptions[]>([]);

  const isOptionDisabled = (option: AssigneeDropDownOptions) => {
    if (option.id === leadUserId || option.role === UserRoles.InternalAdmin) {
      return true;
    }

    return false;
  };

  React.useEffect(() => {
    if (internalUsers) {
      const filteredInternals = internalUsers.filter(
        u => u.entityStatus !== 'ARCHIVED',
      );
      const internalUsersStaged: Array<AssigneeDropDownOptions> = filteredInternals.map(
        internalUser => {
          /* eslint-disable @typescript-eslint/camelcase */
          const {
            given_name,
            family_name,
            picture,
            fallbackColor,
            roles,
          } = internalUser.fields;
          return {
            label: `${given_name} ${family_name}`,
            avatarUrl: picture,
            id: internalUser.id,
            fallbackColor,
            role: roles && roles.length > 0 ? roles[0] : '',
          };
        },
      );

      const sortedInternalOptions = sortBy(internalUsersStaged, 'label');
      setOptions(sortedInternalOptions);
    }
  }, [internalUsers]);

  React.useEffect(() => {
    if (isMultiple && assignees) {
      const assigneesIds = assignees.map(assignee => assignee.id);
      const assigneesIdsSet = new Set(assigneesIds);
      const pendingValueStaged = options.filter(option =>
        assigneesIdsSet.has(option.id),
      );
      setPendingValue(pendingValueStaged);
    } else if (!isMultiple && lead) {
      const pendingValueStaged = options.find(user => user.id === lead.id);
      setPendingValue(pendingValueStaged);
    }
  }, [isMultiple, lead, assignees, options]);

  const handleClose = (
    event: React.ChangeEvent<HTMLInputElement>,
    reason: AutocompleteCloseReason,
  ) => {
    event.preventDefault();
    onClose(event, reason);
  };

  const handleChange = (_, newValue: AssigneeDropDownOptions) => {
    if (newValue) {
      onChange(newValue);
      setPendingValue(newValue);
    }
  };

  return (
    <SelectDropDown
      width={menuWidth}
      isMultiple={isMultiple}
      id={assigneeDropDownId}
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      onClose={handleClose}
      value={isMultiple ? pendingValue : ''}
      onChange={handleChange}
      getOptionDisabled={(option: AssigneeDropDownOptions) =>
        isMultiple && isOptionDisabled(option)
      }
      noOptionsText={noOptionsText}
      options={options}
      placeholder={placeholder}
      {...autocompleteOptions}
    />
  );
};
